import type { IOption } from '@shared/form/types';

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import {
  FIELD_FILTERS_ALLOWED_MODULE,
  type IInitialFilter,
  type ISearchParameters,
  OPERATOR_SORT,
  OPERATOR_TYPE,
} from '@features/filters/types';
import { getFieldBasedPreviousFiltersInitialValues } from '@features/filters/utils/getFieldBasedFiltersInitialValues';
import { hasPartialUpdates } from '@utils/hasPartialUpdates';

export const initialBonusSearchParameters = {
  page: 1,
  size: 10,
  filters: [{ value: true, field: 'enabled', type: OPERATOR_TYPE.EQUAL }],
  sorts: [
    {
      field: 'modified_at',
      order: OPERATOR_SORT.DESC,
    },
  ],
};

interface IInitialState {
  searchParameters: ISearchParameters;
  prevFilters: Record<string, IInitialFilter>;
  dialogState: {
    isConfirmBrandsDialogOpen: boolean;
  };
  activeBonus: {
    enabled: boolean;
    id: null | string;
    name: null | string;
  };
  bonusConfig: {
    type: string;
    activeTab: number;
    isCreateAction: boolean;
    currentBrands: IOption[];
    previousBrands: IOption[];
    brandChangeReason: string;
    isBonusRulesError: boolean;
    isUseConditionsError: boolean;
    isGeneralDetailsError: boolean;
    isOutcomeDetailsError: boolean;
    hasBrandsWithoutAccess: boolean;
    isWageringRequirementsError: boolean;
  };
}

const initialState: IInitialState = {
  searchParameters: initialBonusSearchParameters,
  dialogState: {
    isConfirmBrandsDialogOpen: false,
  },
  activeBonus: {
    id: null,
    name: null,
    enabled: false,
  },
  prevFilters: getFieldBasedPreviousFiltersInitialValues(
    FIELD_FILTERS_ALLOWED_MODULE.BONUS
  ),
  bonusConfig: {
    type: '',
    activeTab: 0,
    currentBrands: [],
    previousBrands: [],
    brandChangeReason: '',
    isCreateAction: false,
    isBonusRulesError: false,
    isUseConditionsError: false,
    isGeneralDetailsError: false,
    isOutcomeDetailsError: false,
    hasBrandsWithoutAccess: false,
    isWageringRequirementsError: false,
  },
};

const bonusesSlice = createSlice({
  initialState,
  name: 'bonusesSlice',
  reducers: {
    setActiveBonus(
      state: IInitialState,
      action: PayloadAction<IInitialState['activeBonus']>
    ) {
      state.activeBonus = action.payload;
    },
    setPrevFilters(
      state: IInitialState,
      action: PayloadAction<Record<string, IInitialFilter>>
    ) {
      state.prevFilters = action.payload;
    },
    setSearchParameters(
      state: IInitialState,
      action: PayloadAction<Partial<ISearchParameters>>
    ) {
      state.searchParameters = { ...state.searchParameters, ...action.payload };
    },
    setBonusDialogState(
      state: IInitialState,
      action: PayloadAction<Partial<IInitialState['dialogState']>>
    ) {
      state.dialogState = { ...state.dialogState, ...action.payload };
    },
    setBonusConfig(
      state: IInitialState,
      action: PayloadAction<Partial<IInitialState['bonusConfig']>>
    ) {
      if (!hasPartialUpdates(state.bonusConfig, action.payload)) {
        return;
      }

      Object.assign(state.bonusConfig, action.payload);
    },
  },
});

export default bonusesSlice.reducer;

export const {
  setPrevFilters,
  setBonusConfig,
  setActiveBonus,
  setSearchParameters,
  setBonusDialogState,
} = bonusesSlice.actions;
